import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { History, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// Permanent record of every login (db.login_events), newest first. Rendered
// standalone (LoginHistory page) and as a SuperAdmin tab.
function LoginHistoryPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/superadmin/login-history`);
      setEvents(res.data?.events || []);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 403
          ? 'Superadmin access only — log in as a superadmin to view login history.'
          : (err?.response?.data?.detail || 'Failed to load login history.')
      );
      toast.error('Could not load login history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Every login, newest first — who logged in, when, how, and from where. Permanent record.
        </p>
        <button
          onClick={fetchHistory}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-200 hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
          <CardTitle className="text-xl flex items-center justify-between">
            <span className="flex items-center gap-2"><History className="w-5 h-5 text-purple-600" /> All Logins</span>
            <Badge className="bg-purple-600">{events.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {error ? (
            <div className="text-center py-10 text-red-600">{error}</div>
          ) : loading && events.length === 0 ? (
            <div className="text-center py-10 text-gray-400">Loading…</div>
          ) : events.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              No logins recorded yet. They'll appear here as users log in.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-4">User</th>
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">Event</th>
                    <th className="py-2 pr-4">Method</th>
                    <th className="py-2 pr-4">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e, i) => (
                    <tr key={`${e.user_id}-${e.timestamp}-${i}`} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{e.email || '(unknown)'}</td>
                      <td className="py-2 pr-4 text-gray-600">{formatWhen(e.timestamp)}</td>
                      <td className="py-2 pr-4">
                        <Badge variant={e.event_type === 'register' ? 'default' : 'outline'} className="w-fit">
                          {e.event_type || 'login'}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4 text-gray-600">{e.method || '—'}</td>
                      <td className="py-2 pr-4 text-gray-400 font-mono text-xs">{e.ip || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default LoginHistoryPanel;
